import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import {
    FileDown, Image as ImageIcon, Layout as LayoutIcon, Settings2,
    Grid, List, BookOpen, Eye, Package, GripVertical, Plus, Trash2, Type
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
    DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates,
    useSortable, verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ─── Utilities ────────────────────────────────────────────────────────────────
const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const base = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;
    return `${base}${url}`;
};

const TEMPLATES = {
    grid: { id: 'grid', name: 'Grid', description: 'Compact product cards in a grid', icon: Grid, itemsPerRowOptions: [2, 3, 4], defaultItemsPerRow: 3 },
    catalog: { id: 'catalog', name: 'Catalog', description: 'Large cards with generous spacing', icon: BookOpen, itemsPerRowOptions: [1, 2], defaultItemsPerRow: 2 },
    minimal: { id: 'minimal', name: 'Minimal', description: 'Clean list with small thumbnails', icon: List, itemsPerRowOptions: [1], defaultItemsPerRow: 1 },
};

const PAGE_SIZES = {
    a4: { label: 'A4', format: 'a4' },
    letter: { label: 'Letter', format: 'letter' },
};

// ─── Sortable Item Component ──────────────────────────────────────────────────
const SortableItem = ({ item, onRemove, onUpdateSection }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.uid });
    const style = { transform: CSS.Transform.toString(transform), transition };

    if (item.type === 'section') {
        return (
            <div ref={setNodeRef} style={style} className="flex items-center gap-2 p-2 bg-amber-50 border-2 border-amber-200 rounded-lg">
                <button {...attributes} {...listeners} className="cursor-grab text-amber-400 hover:text-amber-600 touch-none">
                    <GripVertical size={16} />
                </button>
                <Type size={14} className="text-amber-600 shrink-0" />
                <Input
                    value={item.title}
                    onChange={(e) => onUpdateSection(item.uid, e.target.value)}
                    className="h-8 text-sm font-semibold bg-transparent border-amber-300 flex-1"
                    placeholder="Section Title..."
                />
                <button onClick={() => onRemove(item.uid)} className="text-red-400 hover:text-red-600">
                    <Trash2 size={14} />
                </button>
            </div>
        );
    }

    return (
        <div ref={setNodeRef} style={style} className="flex items-center gap-2 p-2 bg-white border border-stone-200 rounded-lg">
            <button {...attributes} {...listeners} className="cursor-grab text-stone-400 hover:text-stone-600 touch-none">
                <GripVertical size={16} />
            </button>
            <div className="w-8 h-8 rounded bg-stone-100 shrink-0 flex items-center justify-center overflow-hidden">
                {item.image_url ? (
                    <img src={resolveImageUrl(item.image_url)} alt="" className="w-full h-full object-contain" />
                ) : (
                    <ImageIcon size={12} className="text-stone-300" />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-900 truncate">{item.name}</p>
                <p className="text-[10px] text-gray-400 font-mono">{item.product_code}</p>
            </div>
            <button onClick={() => onRemove(item.uid)} className="text-red-400 hover:text-red-600 shrink-0">
                <Trash2 size={14} />
            </button>
        </div>
    );
};

// ─── Live Preview Component ───────────────────────────────────────────────────
const LivePreview = ({ orderedItems, template, itemsPerRow, primaryColor, companyName, companyTagline, companyLogoUrl, contactInfo, showFields, orientation }) => {
    const previewItems = orderedItems.slice(0, template === 'minimal' ? 10 : template === 'catalog' ? 6 : 12);
    const isLandscape = orientation === 'landscape';

    if (orderedItems.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-stone-400 py-16">
                <Package size={48} className="mb-4 opacity-40" />
                <p className="text-lg font-medium">No products arranged</p>
                <p className="text-sm mt-1">Select products, then arrange them in the Arrange tab</p>
            </div>
        );
    }

    return (
        <div
            className="bg-white rounded-lg shadow-md border border-stone-200 overflow-hidden mx-auto"
            style={{ aspectRatio: isLandscape ? '297/210' : '210/297', maxHeight: '600px', maxWidth: isLandscape ? '600px' : '420px' }}
        >
            <div className="h-full flex flex-col text-[10px] overflow-hidden">
                {/* Header */}
                <div className="px-4 pt-3 pb-2 border-b-2 flex items-center gap-3" style={{ borderColor: primaryColor }}>
                    {companyLogoUrl && (
                        <img src={resolveImageUrl(companyLogoUrl)} alt="Logo" className="w-8 h-8 object-contain rounded" onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                    <div>
                        <div className="font-bold text-sm leading-tight" style={{ color: primaryColor }}>{companyName}</div>
                        <div className="text-stone-500 text-[9px]">{companyTagline}</div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 p-3 overflow-hidden">
                    {template === 'minimal' ? (
                        <div className="space-y-1">
                            {previewItems.map((item, i) => {
                                if (item.type === 'section') {
                                    return <div key={i} className="text-[9px] font-bold py-1 mt-1 border-b" style={{ color: primaryColor, borderColor: primaryColor }}>{item.title}</div>;
                                }
                                return (
                                    <div key={i} className="flex items-center gap-2 p-1 rounded border border-stone-100 bg-stone-50/50">
                                        <div className="w-7 h-7 rounded bg-stone-100 shrink-0 flex items-center justify-center overflow-hidden">
                                            {item.image_url ? <img src={resolveImageUrl(item.image_url)} alt="" className="w-full h-full object-contain" /> : <ImageIcon size={8} className="text-stone-300" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            {showFields.name && <div className="font-medium text-stone-800 truncate text-[8px]">{item.name}</div>}
                                            {showFields.description && item.description && <div className="text-[7px] text-stone-500 truncate">{item.description}</div>}
                                        </div>
                                        {showFields.link && item.product_link && <div className="text-[7px] shrink-0" style={{ color: primaryColor }}>→</div>}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="space-y-1.5">
                            {(() => {
                                const items = [];
                                let rowItems = [];
                                const cols = template === 'catalog' ? Math.min(itemsPerRow, 2) : itemsPerRow;

                                previewItems.forEach((item, i) => {
                                    if (item.type === 'section') {
                                        if (rowItems.length > 0) { items.push(<div key={`row-${i}`} className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>{rowItems}</div>); rowItems = []; }
                                        items.push(<div key={i} className="text-[9px] font-bold py-1 mt-1 border-b" style={{ color: primaryColor, borderColor: primaryColor }}>{item.title}</div>);
                                    } else {
                                        rowItems.push(
                                            <div key={i} className="rounded border border-stone-200 bg-stone-50/30 overflow-hidden">
                                                <div className="bg-stone-50 flex items-center justify-center overflow-hidden" style={{ height: template === 'catalog' ? '65px' : '42px' }}>
                                                    {item.image_url ? <img src={resolveImageUrl(item.image_url)} alt="" className="w-full h-full object-contain p-1" /> : <ImageIcon size={12} className="text-stone-300" />}
                                                </div>
                                                <div className="px-1 py-0.5 text-center">
                                                    {showFields.name && <div className="font-semibold text-stone-800 truncate" style={{ fontSize: '7px' }}>{item.name}</div>}
                                                    {showFields.description && item.description && <div className="text-stone-500 truncate" style={{ fontSize: '6px' }}>{item.description}</div>}
                                                    {showFields.link && item.product_link && <div style={{ fontSize: '6px', color: primaryColor }}>View</div>}
                                                </div>
                                            </div>
                                        );
                                        if (rowItems.length >= cols) { items.push(<div key={`row-${i}`} className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>{rowItems}</div>); rowItems = []; }
                                    }
                                });
                                if (rowItems.length > 0) items.push(<div key="last-row" className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>{rowItems}</div>);
                                return items;
                            })()}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-3 py-1.5 text-white text-center text-[7px] truncate" style={{ backgroundColor: primaryColor }}>
                    {contactInfo}
                </div>
            </div>
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const BrochureMaker = ({ user }) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('select');

    // Selections and ordering
    const [selectedProductIds, setSelectedProductIds] = useState(new Set());
    const [orderedItems, setOrderedItems] = useState([]); // [{type:'product',...} | {type:'section', uid, title}]

    // Branding
    const [companyName, setCompanyName] = useState('Shubha Lakshmi Enterprises');
    const [companyTagline, setCompanyTagline] = useState('Quality Products');
    const [companyLogoUrl, setCompanyLogoUrl] = useState('/images/logo.png');
    const [contactInfo, setContactInfo] = useState('Website: https://www.sleonline.in/ | Phone: +91 7597784472 | Email: contact@sleonline.in');

    // Layout
    const [template, setTemplate] = useState('grid');
    const [itemsPerRow, setItemsPerRow] = useState(3);
    const [primaryColor, setPrimaryColor] = useState('#2563eb');
    const [pageSize, setPageSize] = useState('a4');
    const [orientation, setOrientation] = useState('portrait');

    // Toggle fields
    const [showFields, setShowFields] = useState({ name: true, description: true, link: true });

    // DnD
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    useEffect(() => { fetchProducts(); }, []);
    useEffect(() => { setItemsPerRow(TEMPLATES[template].defaultItemsPerRow); }, [template]);

    // Sync orderedItems when selection changes
    useEffect(() => {
        setOrderedItems(prev => {
            const existingProductUids = new Set(prev.filter(i => i.type === 'product').map(i => i.id));
            const newIds = [...selectedProductIds].filter(id => !existingProductUids.has(id));
            const removedIds = [...existingProductUids].filter(id => !selectedProductIds.has(id));

            let updated = prev.filter(i => i.type === 'section' || !removedIds.includes(i.id));
            const newProducts = products.filter(p => newIds.includes(p.id)).map(p => ({ ...p, type: 'product', uid: `p-${p.id}` }));
            return [...updated, ...newProducts];
        });
    }, [selectedProductIds, products]);

    const fetchProducts = async () => {
        try {
            const res = await axios.get(`${API}/finished-goods`);
            const sorted = res.data.sort((a, b) => {
                if (a.image_url && !b.image_url) return -1;
                if (!a.image_url && b.image_url) return 1;
                return a.name.localeCompare(b.name);
            });
            setProducts(sorted);
        } catch (error) {
            toast.error('Failed to fetch products');
        } finally {
            setLoading(false);
        }
    };

    const toggleProductSelection = (id) => {
        const s = new Set(selectedProductIds);
        if (s.has(id)) s.delete(id); else s.add(id);
        setSelectedProductIds(s);
    };

    const handleSelectAllHasImage = () => setSelectedProductIds(new Set(products.filter(p => p.image_url).map(p => p.id)));
    const handleClearSelection = () => { setSelectedProductIds(new Set()); setOrderedItems([]); };

    const addSection = () => {
        const uid = `s-${Date.now()}`;
        setOrderedItems(prev => [...prev, { type: 'section', uid, title: 'New Section' }]);
    };

    const removeItem = (uid) => {
        const item = orderedItems.find(i => i.uid === uid);
        if (item?.type === 'product') {
            const s = new Set(selectedProductIds);
            s.delete(item.id);
            setSelectedProductIds(s);
        }
        setOrderedItems(prev => prev.filter(i => i.uid !== uid));
    };

    const updateSectionTitle = (uid, title) => {
        setOrderedItems(prev => prev.map(i => i.uid === uid ? { ...i, title } : i));
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            setOrderedItems(prev => {
                const oldIdx = prev.findIndex(i => i.uid === active.id);
                const newIdx = prev.findIndex(i => i.uid === over.id);
                return arrayMove(prev, oldIdx, newIdx);
            });
        }
    };

    // ─── Image helper ──────────────────────────────────────────────────────────
    const getBase64ImageFromUrl = async (imageUrl) => {
        try {
            const res = await fetch(resolveImageUrl(imageUrl));
            const blob = await res.blob();
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch {
            return null;
        }
    };

    // ─── PDF Generation ────────────────────────────────────────────────────────
    const generatePDF = async () => {
        const productItems = orderedItems.filter(i => i.type === 'product');
        if (productItems.length === 0) { toast.error('Please select at least one product'); return; }
        toast.info('Generating PDF...');

        try {
            const doc = new jsPDF({ orientation, unit: 'mm', format: PAGE_SIZES[pageSize].format });
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 15;
            let currentPage = 1;

            const addHeaderFooter = async (isFirstPage = false) => {
                doc.setFillColor(primaryColor);
                doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(8);
                doc.text(contactInfo, pageWidth / 2, pageHeight - 6, { align: 'center' });
                doc.text(`Page ${currentPage}`, pageWidth - margin - 5, pageHeight - 6);

                if (isFirstPage) {
                    if (companyLogoUrl) {
                        const logo = await getBase64ImageFromUrl(companyLogoUrl);
                        if (logo) doc.addImage(logo, 'JPEG', margin, 10, 30, 30, undefined, 'FAST');
                    }
                    doc.setTextColor(primaryColor);
                    doc.setFontSize(22);
                    doc.setFont("helvetica", "bold");
                    doc.text(companyName, margin + 35, 22);
                    doc.setTextColor(100, 100, 100);
                    doc.setFontSize(13);
                    doc.setFont("helvetica", "normal");
                    doc.text(companyTagline, margin + 35, 30);
                    doc.setDrawColor(primaryColor);
                    doc.setLineWidth(1);
                    doc.line(margin, 45, pageWidth - margin, 45);
                    return 55;
                } else {
                    doc.setTextColor(primaryColor);
                    doc.setFontSize(10);
                    doc.text(`${companyName} - Product Catalog`, margin, 15);
                    doc.setDrawColor(200, 200, 200);
                    doc.line(margin, 20, pageWidth - margin, 20);
                    return 28;
                }
            };

            // Preload images
            const imageMap = {};
            for (const item of orderedItems) {
                if (item.type === 'product' && item.image_url) {
                    imageMap[item.uid] = await getBase64ImageFromUrl(item.image_url);
                }
            }

            let yPos = await addHeaderFooter(true);
            const contentWidth = pageWidth - margin * 2;

            const newPageIfNeeded = async (needed) => {
                if (yPos + needed > pageHeight - 20) {
                    doc.addPage();
                    currentPage++;
                    yPos = await addHeaderFooter(false);
                }
            };

            if (template === 'minimal') {
                // ── MINIMAL ──
                const rowH = showFields.description ? 20 : 16;
                for (const item of orderedItems) {
                    if (item.type === 'section') {
                        await newPageIfNeeded(14);
                        doc.setTextColor(primaryColor);
                        doc.setFontSize(13);
                        doc.setFont("helvetica", "bold");
                        doc.text(item.title, margin, yPos + 8);
                        doc.setDrawColor(primaryColor);
                        doc.setLineWidth(0.5);
                        doc.line(margin, yPos + 10, pageWidth - margin, yPos + 10);
                        yPos += 14;
                        continue;
                    }
                    await newPageIfNeeded(rowH + 2);
                    const img = imageMap[item.uid];
                    doc.setFillColor(yPos % 2 < 1 ? 250 : 255, yPos % 2 < 1 ? 250 : 255, yPos % 2 < 1 ? 250 : 255);
                    doc.rect(margin, yPos, contentWidth, rowH, 'F');
                    doc.setDrawColor(230, 230, 230);
                    doc.rect(margin, yPos, contentWidth, rowH, 'S');

                    if (img) doc.addImage(img, 'JPEG', margin + 2, yPos + 1, rowH - 2, rowH - 2, undefined, 'FAST');

                    let textX = margin + rowH + 4;
                    if (showFields.name) {
                        doc.setTextColor(30, 30, 30);
                        doc.setFontSize(11);
                        doc.setFont("helvetica", "bold");
                        doc.text(doc.splitTextToSize(item.name, contentWidth - rowH - 45)[0], textX, yPos + 7);
                    }
                    if (showFields.description && item.description) {
                        doc.setTextColor(100, 100, 100);
                        doc.setFontSize(8);
                        doc.setFont("helvetica", "normal");
                        doc.text(doc.splitTextToSize(item.description, contentWidth - rowH - 45)[0], textX, yPos + 13);
                    }
                    if (showFields.link && item.product_link) {
                        doc.setTextColor(primaryColor);
                        doc.setFontSize(8);
                        doc.textWithLink('View Product →', margin + contentWidth - 35, yPos + 10, { url: item.product_link });
                    }
                    yPos += rowH + 2;
                }
            } else {
                // ── GRID / CATALOG ──
                const cols = itemsPerRow;
                const gap = 8;
                const colWidth = (contentWidth - (cols - 1) * gap) / cols;
                const imgH = template === 'catalog' ? 55 : 40;
                const descH = showFields.description ? 8 : 0;
                const linkH = showFields.link ? 8 : 0;
                const nameH = showFields.name ? 8 : 0;
                const itemHeight = imgH + 10 + nameH + descH + linkH;
                let currentCol = 0;

                for (const item of orderedItems) {
                    if (item.type === 'section') {
                        if (currentCol > 0) { yPos += itemHeight + gap; currentCol = 0; }
                        await newPageIfNeeded(16);
                        doc.setTextColor(primaryColor);
                        doc.setFontSize(14);
                        doc.setFont("helvetica", "bold");
                        doc.text(item.title, margin, yPos + 10);
                        doc.setDrawColor(primaryColor);
                        doc.setLineWidth(0.5);
                        doc.line(margin, yPos + 13, pageWidth - margin, yPos + 13);
                        yPos += 18;
                        continue;
                    }

                    await newPageIfNeeded(itemHeight);
                    if (currentCol === 0 && yPos + itemHeight > pageHeight - 20) {
                        doc.addPage(); currentPage++;
                        yPos = await addHeaderFooter(false);
                    }

                    const xPos = margin + currentCol * (colWidth + gap);
                    doc.setDrawColor(220, 220, 220);
                    doc.setFillColor(252, 252, 252);
                    doc.roundedRect(xPos, yPos, colWidth, itemHeight, 2, 2, 'FD');

                    const img = imageMap[item.uid];
                    const imgW = Math.min(colWidth - 10, imgH);
                    if (img) {
                        doc.addImage(img, 'JPEG', xPos + (colWidth - imgW) / 2, yPos + 4, imgW, imgH, undefined, 'FAST');
                    } else {
                        doc.setFillColor(240, 240, 240);
                        doc.rect(xPos + (colWidth - imgW) / 2, yPos + 4, imgW, imgH, 'F');
                        doc.setTextColor(150, 150, 150);
                        doc.setFontSize(9);
                        doc.text('No Image', xPos + colWidth / 2, yPos + imgH / 2 + 4, { align: 'center' });
                    }

                    let textY = yPos + imgH + 8;
                    if (showFields.name) {
                        doc.setTextColor(30, 30, 30);
                        doc.setFontSize(template === 'catalog' ? 11 : 9);
                        doc.setFont("helvetica", "bold");
                        doc.text(doc.splitTextToSize(item.name, colWidth - 6)[0], xPos + colWidth / 2, textY, { align: 'center' });
                        textY += nameH;
                    }
                    if (showFields.description && item.description) {
                        doc.setTextColor(100, 100, 100);
                        doc.setFontSize(8);
                        doc.setFont("helvetica", "normal");
                        doc.text(doc.splitTextToSize(item.description, colWidth - 6)[0], xPos + colWidth / 2, textY, { align: 'center' });
                        textY += descH;
                    }
                    if (showFields.link && item.product_link) {
                        doc.setTextColor(primaryColor);
                        doc.setFontSize(8);
                        doc.textWithLink('View Product', xPos + colWidth / 2 - 8, textY, { url: item.product_link });
                    }

                    currentCol++;
                    if (currentCol >= cols) { currentCol = 0; yPos += itemHeight + gap; }
                }
            }

            doc.save(`Catalog_${companyName.replace(/\s+/g, '_')}.pdf`);
            toast.success('Catalog generated successfully!');
        } catch (error) {
            console.error(error);
            toast.error('Error generating PDF.');
        }
    };

    if (loading) return <div className="p-6">Loading products...</div>;

    const tabs = [
        { id: 'select', label: `Select (${selectedProductIds.size})`, icon: Package },
        { id: 'arrange', label: `Arrange (${orderedItems.length})`, icon: GripVertical },
        { id: 'preview', label: 'Preview', icon: Eye },
    ];

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Brochure Maker</h1>
                    <p className="text-gray-500 mt-1">Select, arrange, preview, and generate your catalog PDF.</p>
                </div>
                <Button onClick={generatePDF} className="gap-2 shrink-0 bg-blue-600 hover:bg-blue-700">
                    <FileDown size={18} />
                    Generate PDF
                </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* ─── Left Column ─── */}
                <div className="lg:col-span-2 space-y-4">
                    {/* Tabs */}
                    <div className="flex gap-1 bg-stone-100 p-1 rounded-lg">
                        {tabs.map(t => {
                            const Icon = t.icon;
                            return (
                                <button
                                    key={t.id}
                                    onClick={() => setActiveTab(t.id)}
                                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-1.5 ${activeTab === t.id ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    <Icon size={15} />
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Select Products */}
                    {activeTab === 'select' && (
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-stone-200">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold text-gray-900">Select Products</h2>
                                <div className="space-x-2">
                                    <Button variant="outline" size="sm" onClick={handleSelectAllHasImage}>Select All w/ Images</Button>
                                    <Button variant="ghost" size="sm" onClick={handleClearSelection} className="text-red-500">Clear</Button>
                                </div>
                            </div>
                            <div className="text-sm text-gray-500 mb-4">Selected: {selectedProductIds.size} of {products.length}</div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[600px] overflow-y-auto pr-2 pb-2">
                                {products.map(product => {
                                    const isSelected = selectedProductIds.has(product.id);
                                    return (
                                        <div
                                            key={product.id}
                                            onClick={() => toggleProductSelection(product.id)}
                                            className={`cursor-pointer rounded-lg border-2 transition-all p-2 flex flex-col ${isSelected ? 'border-blue-500 bg-blue-50/30' : 'border-stone-200 hover:border-blue-300'}`}
                                        >
                                            <div className="relative aspect-square bg-stone-50 rounded-md overflow-hidden mb-2 border border-stone-100 flex items-center justify-center">
                                                <div className="absolute top-1.5 left-1.5 z-10">
                                                    <Checkbox checked={isSelected} className={isSelected ? 'bg-blue-500 border-blue-500' : 'bg-white'} />
                                                </div>
                                                {product.image_url ? (
                                                    <img src={resolveImageUrl(product.image_url)} alt={product.name} className="w-full h-full object-contain p-2" />
                                                ) : (
                                                    <ImageIcon className="text-stone-300" size={24} />
                                                )}
                                            </div>
                                            <p className="text-xs font-medium text-gray-900 line-clamp-2 leading-tight">{product.name}</p>
                                            <p className="text-[10px] text-gray-500 mt-1 font-mono">{product.product_code}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Arrange */}
                    {activeTab === 'arrange' && (
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-stone-200">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold text-gray-900">Arrange Order</h2>
                                <Button variant="outline" size="sm" onClick={addSection} className="gap-1.5">
                                    <Plus size={14} />
                                    Add Section
                                </Button>
                            </div>
                            {orderedItems.length === 0 ? (
                                <div className="text-center text-stone-400 py-12">
                                    <Package size={32} className="mx-auto mb-3 opacity-40" />
                                    <p className="text-sm">Select products first, then arrange them here</p>
                                </div>
                            ) : (
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={orderedItems.map(i => i.uid)} strategy={verticalListSortingStrategy}>
                                        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                                            {orderedItems.map(item => (
                                                <SortableItem key={item.uid} item={item} onRemove={removeItem} onUpdateSection={updateSectionTitle} />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            )}
                        </div>
                    )}

                    {/* Preview */}
                    {activeTab === 'preview' && (
                        <div className="bg-stone-50 p-6 rounded-lg shadow-sm border border-stone-200 flex flex-col items-center justify-center min-h-[500px]">
                            <LivePreview
                                orderedItems={orderedItems}
                                template={template}
                                itemsPerRow={itemsPerRow}
                                primaryColor={primaryColor}
                                companyName={companyName}
                                companyTagline={companyTagline}
                                companyLogoUrl={companyLogoUrl}
                                contactInfo={contactInfo}
                                showFields={showFields}
                                orientation={orientation}
                            />
                            {orderedItems.length > 0 && (
                                <p className="text-xs text-stone-400 text-center mt-3">
                                    Approximate preview of first page · {orderedItems.filter(i => i.type === 'product').length} products, {orderedItems.filter(i => i.type === 'section').length} sections
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* ─── Right Column: Settings ─── */}
                <div className="space-y-5">
                    {/* Template */}
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-stone-200">
                        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2"><LayoutIcon size={18} /> Template</h2>
                        <div className="space-y-2">
                            {Object.values(TEMPLATES).map(t => {
                                const Icon = t.icon;
                                const isActive = template === t.id;
                                return (
                                    <button key={t.id} onClick={() => setTemplate(t.id)}
                                        className={`w-full flex items-center gap-3 p-2.5 rounded-lg border-2 transition-all text-left ${isActive ? 'border-blue-500 bg-blue-50/40' : 'border-stone-200 hover:border-blue-300'}`}>
                                        <div className={`p-1.5 rounded-md ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-stone-100 text-stone-500'}`}><Icon size={16} /></div>
                                        <div>
                                            <div className={`text-sm font-semibold ${isActive ? 'text-blue-700' : 'text-gray-800'}`}>{t.name}</div>
                                            <div className="text-xs text-gray-500">{t.description}</div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Layout Options */}
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-stone-200">
                        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2"><Settings2 size={18} /> Layout</h2>
                        <div className="space-y-4">
                            {/* Items per row */}
                            {TEMPLATES[template].itemsPerRowOptions.length > 1 && (
                                <div className="space-y-1.5">
                                    <Label>Items per Row</Label>
                                    <div className="flex gap-2">
                                        {TEMPLATES[template].itemsPerRowOptions.map(n => (
                                            <Button key={n} variant={itemsPerRow === n ? 'default' : 'outline'} className={itemsPerRow === n ? 'bg-blue-600' : ''} onClick={() => setItemsPerRow(n)} size="sm">{n}</Button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Page size & orientation */}
                            <div className="space-y-1.5">
                                <Label>Page Size</Label>
                                <div className="flex gap-2">
                                    {Object.entries(PAGE_SIZES).map(([k, v]) => (
                                        <Button key={k} variant={pageSize === k ? 'default' : 'outline'} className={pageSize === k ? 'bg-blue-600' : ''} onClick={() => setPageSize(k)} size="sm">{v.label}</Button>
                                    ))}
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Orientation</Label>
                                <div className="flex gap-2">
                                    {['portrait', 'landscape'].map(o => (
                                        <Button key={o} variant={orientation === o ? 'default' : 'outline'} className={orientation === o ? 'bg-blue-600' : ''} onClick={() => setOrientation(o)} size="sm" style={{ textTransform: 'capitalize' }}>{o}</Button>
                                    ))}
                                </div>
                            </div>

                            {/* Color */}
                            <div className="space-y-1.5">
                                <Label>Primary Color</Label>
                                <div className="flex gap-2 items-center">
                                    <Input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="w-10 h-9 p-1 cursor-pointer" />
                                    <Input value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="flex-1 font-mono uppercase text-xs" />
                                </div>
                            </div>

                            {/* Toggle fields */}
                            <div className="space-y-1.5">
                                <Label>Show in PDF</Label>
                                <div className="space-y-2">
                                    {[{ key: 'name', label: 'Product Name' }, { key: 'description', label: 'Description' }, { key: 'link', label: 'Product Link' }].map(f => (
                                        <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer">
                                            <Checkbox checked={showFields[f.key]} onCheckedChange={(v) => setShowFields(prev => ({ ...prev, [f.key]: !!v }))} />
                                            {f.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Branding */}
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-stone-200">
                        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2"><Settings2 size={18} /> Branding</h2>
                        <div className="space-y-3">
                            <div className="space-y-1"><Label>Company Name</Label><Input value={companyName} onChange={e => setCompanyName(e.target.value)} /></div>
                            <div className="space-y-1"><Label>Tagline</Label><Input value={companyTagline} onChange={e => setCompanyTagline(e.target.value)} /></div>
                            <div className="space-y-1">
                                <Label>Logo URL</Label>
                                <Input value={companyLogoUrl} onChange={e => setCompanyLogoUrl(e.target.value)} />
                                {companyLogoUrl && (
                                    <div className="mt-1.5 h-14 bg-stone-50 border border-stone-200 rounded flex items-center justify-center">
                                        <img src={resolveImageUrl(companyLogoUrl)} alt="Logo" className="max-h-10 max-w-full object-contain" />
                                    </div>
                                )}
                            </div>
                            <div className="space-y-1"><Label>Footer Contact</Label><Input value={contactInfo} onChange={e => setContactInfo(e.target.value)} /></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BrochureMaker;
